import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { GridComponent, LegendComponent, TooltipComponent, DataZoomComponent } from 'echarts/components';
import { BarChart, LineChart } from 'echarts/charts';
import * as echarts from 'echarts/core';
import { CanvasRenderer } from 'echarts/renderers';
import ReactEchart from '../../../../components/common/ReactEchart';
import { useAppContext } from '../../../../providers/AppProvider';
import StatisticsCardLoading from '../../common/statistic-chart-card/StatisticsChartCardLoading';
import StatisticsChartEmpty from '../../common/StatisticsChartEmpty';
import { STATISTICS_EMPTY_CHART, STATISTICS_EMPTY_CHART_COMPARE, STATISTICS_EMPTY_CHART_COMPARE_IRREGULAR_PERIOD } from '../../common/lib/statisticsEmptyStateCopy';
import useStatisticsChartTheme from '../../common/hooks/useStatisticsChartTheme';
import { getStatisticsAccentColor } from '../../common/lib/statisticsChartPalette';
import {
	buildStatisticsLineCompareTimeline,
	buildStatisticsLineCompareMultiMetricTimeline,
} from '../../filters/lib/statisticsCompareHelpers';
import { buildPeriodYoyLineSeries, normalizePeriodYoyPayload, PERIOD_YOY_GRANULARITY, resolvePeriodYoyGrid, isFutureCalendarPeriodIndex } from '../../filters/lib/statisticsPeriodYoyHelpers';
import { getPrimaryPhaseRowMetricValue, isPrimaryPhaseCurrencyMetric, ORDER_PRIMARY_PHASE_METRICS } from '../pipeline/lib/orderPrimaryPhaseMetric';
import { getOrderStatisticsPrimaryPhase } from '../pipeline/lib/orderPrimaryPhaseConfig';
import StatisticsChartFrame from '../../common/StatisticsChartFrame';
import { applyChartSummaryLegend } from '../shared/lib/chartSummaryLegend';
import { resolvePeriodCompareCategoryLabel, tightenPreviewChartGrid } from './lib/trendChartOptionShared';
import { getPrimaryPhaseChartTheme } from '../pipeline/lib/orderPrimaryPhaseChartTheme';
import {
	isTrendBarChart,
	isTrendMapChart,
	TREND_CHART_TYPE,
	TREND_LINE_SHAPE,
	TREND_Y_AXIS_METRIC,
	resolveLineCompareActivePanels,
} from './lib/trendChartConfig';
import { normalizeTrendYAxisMetrics, resolveTrendPeriodValueField, resolvePrimaryTrendYAxisMetric } from './lib/trendYAxisMetrics';
import { normalizeMetricSeriesTypes, resolveMetricSeriesDefaultType } from '../shared/lib/metricSeriesTypes';
import { resolveDimensionTrendChartHeight } from './lib/trendChartAxisLayout';
import {
	buildDimensionAxisSnapshotLineOption,
	buildDimensionCompareLineOption,
	buildPeriodCompareLineOption,
	buildYearMonthsCompareLineOption,
} from './lib/trendChartOptionBuilders';
import {
	buildMultiMetricDimensionAxisSnapshotLineOption,
	buildMultiMetricDimensionCompareLineOption,
	buildMultiMetricPeriodCompareLineOption,
	buildMultiMetricYearMonthsCompareLineOption,
} from './lib/trendMultiMetricOptionBuilders';
import { CHART_BAR_ORIENTATION } from '../shared/lib/barOrientation';
import TrendMapChart from './TrendMapChart';
import { isTrendDimensionAxisMode, TREND_AXIS_MODE } from '../../filters/lib/statisticsTrendDimension';
import usePreviewChartPaintGate from '../shared/hooks/usePreviewChartPaintGate';
import {
	parseCompareSeriesMetricLabel,
	resolveLineComparePeriodTimelineDrill,
	resolveLineCompareYearMonthsDrillFromChartClick,
} from './lib/resolveTrendTimeDrill';

echarts.use([TooltipComponent, GridComponent, LegendComponent, DataZoomComponent, LineChart, BarChart, CanvasRenderer]);

const TREND_ECHART_OPTS = Object.freeze({ renderer: 'canvas' });

const TrendChart = ({
	orders = [],
	highlightProvinceNames = [],
	highlightAnchorCityRows = [],
	compares = [],
	trendYearMonthsMode = false,
	trendDualFullPeriod = false,
	trendGranularity = PERIOD_YOY_GRANULARITY.MONTH,
	trendMonthIndices = null,
	trendPeriodIndices = null,
	trendPeriodYoy = null,
	trendDimensionCountSeries = null,
	trendDimensionTotalPriceSeries = null,
	trendDimensionSeriesByMetric = null,
	trendDimensionFieldLabel = '',
	trendAxisMode = TREND_AXIS_MODE.TIME,
	trendDateField = 'createdAt',
	trendYAxisMetric = TREND_Y_AXIS_METRIC.COUNT,
	trendYAxisMetrics = null,
	trendChartType = TREND_CHART_TYPE.LINE,
	trendLineShape = TREND_LINE_SHAPE.SMOOTH,
	trendBarOrientation = CHART_BAR_ORIENTATION.VERTICAL,
	trendMetricSeriesTypes = null,
	trendYearSeriesColors = null,
	selectedPhase,
	isLoading = false,
	animationKey = 'default',
	height = '9.5rem',
	lockChartHeight = false,
	previewHeight = null,
	fillPreview = false,
	showPointLabels = true,
	enableOrderDrill = false,
	onDrillSelect,
	compareAnalysisDisabled = false,
	compareAnalysisDisabledHint = '',
	allowChartWithoutCompareAnalysis = false,
}) => {
	const { getThemeColor } = useAppContext();
	const { isLight } = useStatisticsChartTheme();
	const chartTheme = useMemo(() => getPrimaryPhaseChartTheme(isLight), [isLight]);
	const accentColor = useMemo(() => getStatisticsAccentColor(isLight, getThemeColor), [getThemeColor, isLight]);
	const isPreview = Boolean(previewHeight) || fillPreview;
	const previewFrameStyle = useMemo(() => (isPreview ? { height: '100%', width: '100%', minHeight: 0 } : null), [isPreview]);
	const isYearMonthsMode = trendYearMonthsMode;
	const isDimensionAxisMode = isTrendDimensionAxisMode(trendAxisMode);
	const isMapChart = isTrendMapChart(trendChartType);
	const activeYAxisMetrics = useMemo(() => normalizeTrendYAxisMetrics(trendYAxisMetrics ?? trendYAxisMetric), [trendYAxisMetrics, trendYAxisMetric]);
	const isMultiMetric = activeYAxisMetrics.length > 1;
	const resolvedMetricSeriesTypes = useMemo(
		() =>
			normalizeMetricSeriesTypes(activeYAxisMetrics, trendMetricSeriesTypes ?? {}, {
				defaultType: resolveMetricSeriesDefaultType(trendChartType, isTrendBarChart),
			}),
		[activeYAxisMetrics, trendChartType, trendMetricSeriesTypes],
	);
	const primaryYAxisMetric = useMemo(() => resolvePrimaryTrendYAxisMetric(activeYAxisMetrics), [activeYAxisMetrics]);
	const isAmountYAxis = primaryYAxisMetric === TREND_Y_AXIS_METRIC.TOTAL_PRICE || primaryYAxisMetric === 'amount';
	const isCurrencyYAxis = isPrimaryPhaseCurrencyMetric(primaryYAxisMetric);

	const dimensionSeriesByMetric = useMemo(() => {
		if (trendDimensionSeriesByMetric) return trendDimensionSeriesByMetric;
		return {
			[ORDER_PRIMARY_PHASE_METRICS.COUNT]: trendDimensionCountSeries,
			[ORDER_PRIMARY_PHASE_METRICS.TOTAL_PRICE]: trendDimensionTotalPriceSeries,
		};
	}, [trendDimensionCountSeries, trendDimensionSeriesByMetric, trendDimensionTotalPriceSeries]);

	const activeDimensionSeries = isMultiMetric
		? (dimensionSeriesByMetric[primaryYAxisMetric] ?? trendDimensionCountSeries)
		: (dimensionSeriesByMetric[primaryYAxisMetric] ?? (isAmountYAxis ? trendDimensionTotalPriceSeries : trendDimensionCountSeries));

	const dimensionDrillSourceSeries = useMemo(() => {
		if (isMultiMetric) {
			return dimensionSeriesByMetric[primaryYAxisMetric] ?? activeDimensionSeries;
		}
		return activeDimensionSeries;
	}, [activeDimensionSeries, dimensionSeriesByMetric, isMultiMetric, primaryYAxisMetric]);

	const dimensionDrillRows = useMemo(() => {
		if (!dimensionDrillSourceSeries) return [];
		if (dimensionDrillSourceSeries.rows?.length) return dimensionDrillSourceSeries.rows;
		if (dimensionDrillSourceSeries.axisKeys?.length) {
			return dimensionDrillSourceSeries.axisLabels.map((label, index) => ({
				key: dimensionDrillSourceSeries.axisKeys[index],
				label,
			}));
		}
		return dimensionDrillSourceSeries.axisLabels.map((label) => ({ key: null, label }));
	}, [dimensionDrillSourceSeries]);

	const [highlightedSummary, setHighlightedSummary] = useState(null);
	const [, setHighlightToken] = useState(null);
	const { showPaintSkeleton, onChartReady: onPreviewChartReady } = usePreviewChartPaintGate(
		isPreview && !isLoading,
		animationKey,
	);

	useEffect(() => {
		setHighlightedSummary(null);
		setHighlightToken(null);
	}, [animationKey]);

	const handleDimensionDrillClick = useCallback(
		(params) => {
			if (!isDimensionAxisMode) return;
			const dataIndex = params?.dataIndex;
			if (dataIndex == null || dataIndex < 0) return;

			let rows = dimensionDrillRows;
			if (isMultiMetric && params?.seriesIndex != null) {
				const metric = activeYAxisMetrics[params.seriesIndex];
				const series = metric ? dimensionSeriesByMetric[metric] : null;
				if (series?.axisKeys?.length) {
					rows = series.axisLabels.map((label, index) => ({
						key: series.axisKeys[index],
						label,
					}));
				}
			}

			const row = rows[dataIndex];
			if (!row?.key) return;

			if (enableOrderDrill && onDrillSelect) {
				onDrillSelect({
					kind: 'trend',
					trendAxisMode,
					dimensionKey: row.key,
					label: row.label,
					metricLabel: parseCompareSeriesMetricLabel(params?.seriesName) || undefined,
				});
				return;
			}

			const sourceRow =
				(dimensionDrillSourceSeries?.rows ?? []).find((item) => item.key === row.key) ??
				(dimensionDrillSourceSeries?.rows ?? [])[dataIndex] ??
				row;
			const token = `dimension:${row.key}`;
			const countSeries = dimensionSeriesByMetric[ORDER_PRIMARY_PHASE_METRICS.COUNT] ?? trendDimensionCountSeries;
			const priceSeries = dimensionSeriesByMetric[ORDER_PRIMARY_PHASE_METRICS.TOTAL_PRICE] ?? trendDimensionTotalPriceSeries;
			const multiPeriodSeries = countSeries?.isMultiPeriod
				? countSeries.series
				: priceSeries?.isMultiPeriod
					? priceSeries.series
					: null;

			setHighlightToken((prev) => {
				if (prev === token) {
					setHighlightedSummary(null);
					return null;
				}
				if (multiPeriodSeries?.length > 1) {
					const orderCounts = multiPeriodSeries.map((series, index) => {
						const countValues = countSeries?.series?.[index]?.values ?? series.values;
						return Number(countValues?.[dataIndex]) || 0;
					});
					const salesTotals = multiPeriodSeries.map((_, index) => {
						const priceValues = priceSeries?.series?.[index]?.values;
						return Number(priceValues?.[dataIndex]) || 0;
					});
					setHighlightedSummary({
						salesTotal: salesTotals,
						orderCount: orderCounts,
						compareLabels: multiPeriodSeries.map((series, index) => series.label || `对比${index + 1}`),
					});
					return token;
				}
				setHighlightedSummary({
					salesTotal: Number(sourceRow.totalPrice) || Number(priceSeries?.values?.[dataIndex]) || 0,
					orderCount: Number(sourceRow.count) || Number(countSeries?.values?.[dataIndex]) || 0,
				});
				return token;
			});
		},
		[
			activeYAxisMetrics,
			dimensionDrillRows,
			dimensionDrillSourceSeries,
			dimensionSeriesByMetric,
			enableOrderDrill,
			isDimensionAxisMode,
			isMultiMetric,
			onDrillSelect,
			trendAxisMode,
			trendDimensionCountSeries,
			trendDimensionTotalPriceSeries,
		],
	);

	// onEvents 引用变化会触发 echarts-for-react dispose+重建，换色时柱高会重播动画
	const handleDimensionDrillClickRef = useRef(handleDimensionDrillClick);
	handleDimensionDrillClickRef.current = handleDimensionDrillClick;
	const dimensionDrillEvents = useMemo(
		() => ({
			click: (...args) => handleDimensionDrillClickRef.current?.(...args),
		}),
		[],
	);

	const dimensionChartHeight = useMemo(
		() => (isDimensionAxisMode ? resolveDimensionTrendChartHeight(trendAxisMode, height, activeDimensionSeries?.axisLabels ?? []) : height),
		[activeDimensionSeries?.axisLabels, height, isDimensionAxisMode, trendAxisMode],
	);

	const isDimensionMultiPeriod = Boolean(
		(isMultiMetric ? dimensionSeriesByMetric[primaryYAxisMetric] : activeDimensionSeries)?.isMultiPeriod &&
		(isMultiMetric ? dimensionSeriesByMetric[primaryYAxisMetric] : activeDimensionSeries)?.series?.length > 1,
	);
	const isDimensionChartReady = useMemo(() => {
		if (!isDimensionAxisMode) return false;
		if (isMultiMetric) {
			const readySeries = activeYAxisMetrics.map((metric) => dimensionSeriesByMetric[metric]).filter(Boolean);
			if (!readySeries.length) return false;
			return readySeries.some((series) =>
				series.isMultiPeriod ? Boolean(series.series?.length && series.axisLabels?.length) : Boolean(series.values?.length && series.axisLabels?.length),
			);
		}
		return isDimensionMultiPeriod
			? Boolean(activeDimensionSeries?.series?.length && activeDimensionSeries?.axisLabels?.length)
			: Boolean(activeDimensionSeries?.values?.length && activeDimensionSeries?.axisLabels?.length);
	}, [activeDimensionSeries, activeYAxisMetrics, dimensionSeriesByMetric, isDimensionAxisMode, isDimensionMultiPeriod, isMultiMetric]);
	const showDualFullPeriodGrid = isYearMonthsMode && trendDualFullPeriod;
	const activePanels = useMemo(() => resolveLineCompareActivePanels(activeYAxisMetrics, { dualFullPeriod: showDualFullPeriodGrid }), [activeYAxisMetrics, showDualFullPeriodGrid]);
	const showPanelTitles = activePanels.length > 1;

	const normalizedYoy = useMemo(() => normalizePeriodYoyPayload(trendPeriodYoy), [trendPeriodYoy]);

	const multiMetricTimeline = useMemo(() => {
		if (isYearMonthsMode || !isMultiMetric) return null;
		return buildStatisticsLineCompareMultiMetricTimeline({
			compares,
			metrics: activeYAxisMetrics,
			selectedPhase,
			getMetricValue: getPrimaryPhaseRowMetricValue,
		});
	}, [activeYAxisMetrics, compares, isMultiMetric, isYearMonthsMode, selectedPhase]);

	const timelines = useMemo(() => {
		if (isYearMonthsMode || isMultiMetric) return null;
		return buildStatisticsLineCompareTimeline({
			compares,
			activeMetric: primaryYAxisMetric,
			selectedPhase,
			getMetricValue: getPrimaryPhaseRowMetricValue,
		});
	}, [compares, isMultiMetric, isYearMonthsMode, primaryYAxisMetric, selectedPhase]);

	const periodYoyGranularity = trendGranularity;
	const periodYoyIndices = trendPeriodIndices ?? trendMonthIndices;

	const periodTimelinePoints = useMemo(() => {
		if (isDimensionAxisMode || isYearMonthsMode) return [];
		return isMultiMetric ? (multiMetricTimeline ?? []) : (timelines ?? []);
	}, [isDimensionAxisMode, isMultiMetric, isYearMonthsMode, multiMetricTimeline, timelines]);

	const handleTimeDrillClick = useCallback(
		(params) => {
			if (isDimensionAxisMode || isMapChart) return;

			const drill = isYearMonthsMode
				? resolveLineCompareYearMonthsDrillFromChartClick(params, {
						periodIndices: periodYoyIndices,
						granularity: periodYoyGranularity,
					})
				: resolveLineComparePeriodTimelineDrill({
						points: periodTimelinePoints,
						dataIndex: params?.dataIndex,
						seriesName: params?.seriesName,
					});

			if (enableOrderDrill && onDrillSelect) {
				if (!drill) return;
				onDrillSelect({
					...drill,
					dateField: trendDateField === 'completedAt' ? 'completedAt' : 'createdAt',
				});
				return;
			}

			if (isYearMonthsMode) {
				const dataIndex = params?.dataIndex;
				if (dataIndex == null || dataIndex < 0) return;
				const periodIndex = Array.isArray(periodYoyIndices) && periodYoyIndices.length ? periodYoyIndices[dataIndex] : dataIndex;
				const years = normalizedYoy?.years ?? [];
				if (!years.length) return;
				if (years.some((year) => isFutureCalendarPeriodIndex(year, periodIndex, periodYoyGranularity))) return;
				const grid = resolvePeriodYoyGrid(normalizedYoy, selectedPhase, periodYoyGranularity);
				const salesTotals = [];
				const orderCounts = [];
				const compareLabels = [];
				for (const year of years) {
					const cell = grid?.[year]?.[periodIndex];
					salesTotals.push(Number(cell?.totalPrice) || 0);
					orderCounts.push(Number(cell?.count) || 0);
					compareLabels.push(`${year}年`);
				}
				const token = `yoy:period:${periodIndex}`;
				setHighlightToken((prev) => {
					if (prev === token) {
						setHighlightedSummary(null);
						return null;
					}
					setHighlightedSummary({
						salesTotal: salesTotals.length > 1 ? salesTotals : salesTotals[0] ?? 0,
						orderCount: orderCounts.length > 1 ? orderCounts : orderCounts[0] ?? 0,
						compareLabels: salesTotals.length > 1 ? compareLabels : undefined,
					});
					return token;
				});
				return;
			}

			const point = periodTimelinePoints[params?.dataIndex];
			if (!point) return;
			const compare =
				(compares ?? []).find((item) => item.key === point.key) ??
				(compares ?? []).find((item) => item.periodStartDate === point.startDate && item.periodEndDate === point.endDate);
			const overview = compare?.lensData?.overview ?? compare?.overview ?? null;
			const phaseRow = selectedPhase ? compare?.lensData?.phases?.[selectedPhase] ?? null : null;
			const row = phaseRow ?? overview;
			const token = `period:${point.key ?? point.startDate ?? params?.dataIndex}`;
			setHighlightToken((prev) => {
				if (prev === token) {
					setHighlightedSummary(null);
					return null;
				}
				setHighlightedSummary({
					salesTotal: getPrimaryPhaseRowMetricValue(row, ORDER_PRIMARY_PHASE_METRICS.TOTAL_PRICE),
					orderCount: getPrimaryPhaseRowMetricValue(row, ORDER_PRIMARY_PHASE_METRICS.COUNT),
				});
				return token;
			});
		},
		[
			compares,
			enableOrderDrill,
			isDimensionAxisMode,
			isMapChart,
			isYearMonthsMode,
			normalizedYoy,
			onDrillSelect,
			periodTimelinePoints,
			periodYoyGranularity,
			periodYoyIndices,
			selectedPhase,
			trendDateField,
		],
	);

	const handleTimeDrillClickRef = useRef(handleTimeDrillClick);
	handleTimeDrillClickRef.current = handleTimeDrillClick;
	const timeDrillEvents = useMemo(
		() => ({
			click: (...args) => handleTimeDrillClickRef.current?.(...args),
		}),
		[],
	);
	const chartDrillEvents = isDimensionAxisMode ? dimensionDrillEvents : timeDrillEvents;

	const yearMonthsSeriesByField = useMemo(() => {
		if (!isYearMonthsMode) return null;
		if (isMultiMetric) return null;
		if (trendDualFullPeriod) {
			return Object.fromEntries(
				activePanels.map((panel) => [
					panel.key,
					buildPeriodYoyLineSeries({
						periodYoy: normalizedYoy,
						selectedPhase,
						granularity: panel.granularity,
						valueField: panel.valueField,
						isLight,
					}),
				]),
			);
		}
		const panel = activePanels[0];
		if (!panel) return null;
		const seriesKey = panel.valueField ?? resolveTrendPeriodValueField(primaryYAxisMetric);
		return {
			[seriesKey]: buildPeriodYoyLineSeries({
				periodYoy: normalizedYoy,
				selectedPhase,
				granularity: periodYoyGranularity,
				valueField: panel.valueField ?? resolveTrendPeriodValueField(primaryYAxisMetric),
				periodIndices: periodYoyIndices,
				isLight,
			}),
		};
	}, [
		activePanels,
		isLight,
		isMultiMetric,
		isYearMonthsMode,
		trendDualFullPeriod,
		normalizedYoy,
		periodYoyGranularity,
		periodYoyIndices,
		primaryYAxisMetric,
		selectedPhase,
	]);

	const yearMonthsSeriesByMetric = useMemo(() => {
		if (!isYearMonthsMode || !isMultiMetric) return null;
		const buildForPanel = (panel = {}) =>
			Object.fromEntries(
				activeYAxisMetrics.map((metric) => [
					metric,
					buildPeriodYoyLineSeries({
						periodYoy: normalizedYoy,
						selectedPhase,
						granularity: panel.granularity ?? periodYoyGranularity,
						valueField: resolveTrendPeriodValueField(metric),
						// 双整年模式各 panel 展示完整月/季轴；其余模式保留锚点选定的月份子集
						periodIndices: trendDualFullPeriod && panel.granularity ? undefined : periodYoyIndices,
						isLight,
					}),
				]),
			);
		if (trendDualFullPeriod) {
			return Object.fromEntries(activePanels.map((panel) => [panel.key, buildForPanel(panel)]));
		}
		return { single: buildForPanel() };
	}, [
		activePanels,
		activeYAxisMetrics,
		isLight,
		isMultiMetric,
		isYearMonthsMode,
		trendDualFullPeriod,
		normalizedYoy,
		periodYoyGranularity,
		periodYoyIndices,
		selectedPhase,
	]);

	const hasAnchor = useMemo(() => compares.some((item) => item.kind === 'anchor'), [compares]);
	const customCompareCount = useMemo(() => compares.filter((item) => item.kind === 'custom').length, [compares]);

	const panelOptions = useMemo(() => {
		if (isDimensionAxisMode && isDimensionChartReady) {
			return activePanels.map((panel) => {
				if (isMultiMetric) {
					if (isDimensionMultiPeriod) {
						return {
							...panel,
							option: buildMultiMetricDimensionCompareLineOption({
								dimensionSeriesByMetric: dimensionSeriesByMetric,
								metrics: activeYAxisMetrics,
								chartTheme,
								showPointLabels,
								dimensionAxisMode: trendAxisMode,
								chartType: trendChartType,
								lineShape: trendLineShape,
								barOrientation: trendBarOrientation,
								metricSeriesTypes: resolvedMetricSeriesTypes,
								isLight,
								accentColor,
							}),
						};
					}
					const snapshotSeries = Object.fromEntries(activeYAxisMetrics.map((metric) => [metric, dimensionSeriesByMetric[metric]]).filter(([, series]) => series));
					const axisLabels = snapshotSeries[activeYAxisMetrics[0]]?.axisLabels ?? [];
					return {
						...panel,
						option: buildMultiMetricDimensionAxisSnapshotLineOption({
							axisLabels,
							seriesByMetric: snapshotSeries,
							metrics: activeYAxisMetrics,
							fieldLabel: trendDimensionFieldLabel,
							chartTheme,
							accentColor,
							showPointLabels,
							dimensionAxisMode: trendAxisMode,
							chartType: trendChartType,
							lineShape: trendLineShape,
							barOrientation: trendBarOrientation,
							metricSeriesTypes: resolvedMetricSeriesTypes,
							isLight,
						}),
					};
				}

				const dimensionSeries = dimensionSeriesByMetric[primaryYAxisMetric] ?? activeDimensionSeries;
				if (isDimensionMultiPeriod) {
					return {
						...panel,
						option: buildDimensionCompareLineOption({
							dimensionSeries,
							chartMetric: panel.chartMetric ?? primaryYAxisMetric,
							metricLabel: panel.title,
							currencyMetric: panel.currencyMetric ?? isCurrencyYAxis,
							chartTheme,
							showPointLabels,
							dimensionAxisMode: trendAxisMode,
							chartType: trendChartType,
							lineShape: trendLineShape,
							barOrientation: trendBarOrientation,
							isLight,
						}),
					};
				}
				return {
					...panel,
					option: buildDimensionAxisSnapshotLineOption({
						axisLabels: dimensionSeries?.axisLabels ?? [],
						values: dimensionSeries?.values ?? [],
						chartMetric: panel.chartMetric ?? primaryYAxisMetric,
						metricLabel: panel.title,
						fieldLabel: trendDimensionFieldLabel,
						currencyMetric: panel.currencyMetric ?? isCurrencyYAxis,
						chartTheme,
						accentColor,
						showPointLabels,
						dimensionAxisMode: trendAxisMode,
						chartType: trendChartType,
						lineShape: trendLineShape,
						barOrientation: trendBarOrientation,
						isLight,
					}),
				};
			});
		}

		if (isYearMonthsMode && isMultiMetric) {
			return activePanels.map((panel) => ({
				...panel,
				option: buildMultiMetricYearMonthsCompareLineOption({
					yearMonthsSeriesByMetric: trendDualFullPeriod ? yearMonthsSeriesByMetric?.[panel.key] : yearMonthsSeriesByMetric?.single,
					metrics: activeYAxisMetrics,
					granularity: panel.granularity ?? periodYoyGranularity,
					chartTheme,
					accentColor,
					showPointLabels,
					chartType: trendChartType,
					lineShape: trendLineShape,
					barOrientation: trendBarOrientation,
					metricSeriesTypes: resolvedMetricSeriesTypes,
					isLight,
					yearSeriesColors: trendYearSeriesColors ?? {},
				}),
			}));
		}

		if (isYearMonthsMode && trendDualFullPeriod) {
			return activePanels.map((panel) => {
				const yearMonthsSeries = yearMonthsSeriesByField?.[panel.key];
				return {
					...panel,
					option: buildYearMonthsCompareLineOption({
						yearMonthsSeries,
						valueField: panel.valueField,
						granularity: panel.granularity,
						chartTheme,
						currencyMetric: panel.currencyMetric,
						accentColor,
						showPointLabels,
						chartType: trendChartType,
						lineShape: trendLineShape,
						barOrientation: trendBarOrientation,
						isLight,
						yearSeriesColors: trendYearSeriesColors ?? {},
					}),
				};
			});
		}

		return activePanels.map((panel) => {
			if (isYearMonthsMode) {
				const seriesKey = panel.valueField ?? resolveTrendPeriodValueField(primaryYAxisMetric);
				const yearMonthsSeries = yearMonthsSeriesByField?.[seriesKey];
				return {
					...panel,
					option: buildYearMonthsCompareLineOption({
						yearMonthsSeries,
						valueField: panel.valueField ?? resolveTrendPeriodValueField(primaryYAxisMetric),
						granularity: panel.granularity ?? periodYoyGranularity,
						chartTheme,
						currencyMetric: panel.currencyMetric ?? isCurrencyYAxis,
						accentColor,
						showPointLabels,
						chartType: trendChartType,
						lineShape: trendLineShape,
						barOrientation: trendBarOrientation,
						metricSeriesTypes: resolvedMetricSeriesTypes,
						isLight,
						yearSeriesColors: trendYearSeriesColors ?? {},
					}),
				};
			}

			if (isMultiMetric) {
				if (!multiMetricTimeline?.length) {
					return { ...panel, option: null, timeline: multiMetricTimeline };
				}
				return {
					...panel,
					timeline: multiMetricTimeline,
					option: buildMultiMetricPeriodCompareLineOption({
						timeline: multiMetricTimeline,
						metrics: activeYAxisMetrics,
						chartTheme,
						accentColor,
						showPointLabels,
						chartType: trendChartType,
						lineShape: trendLineShape,
						barOrientation: trendBarOrientation,
						metricSeriesTypes: resolvedMetricSeriesTypes,
						isLight,
						yearSeriesColors: trendYearSeriesColors ?? {},
					}),
				};
			}

			const timeline = timelines;
			if (!timeline?.length) {
				return { ...panel, option: null, timeline };
			}

			return {
				...panel,
				timeline,
				option: buildPeriodCompareLineOption({
					timeline,
					chartMetric: panel.chartMetric ?? primaryYAxisMetric,
					metricLabel: panel.title,
					currencyMetric: panel.currencyMetric ?? isCurrencyYAxis,
					chartTheme,
					accentColor,
					showPointLabels,
					chartType: trendChartType,
					lineShape: trendLineShape,
					barOrientation: trendBarOrientation,
					isLight,
					yearSeriesColors: trendYearSeriesColors ?? {},
				}),
			};
		});
	}, [
		accentColor,
		activeDimensionSeries,
		activePanels,
		activeYAxisMetrics,
		chartTheme,
		dimensionSeriesByMetric,
		isCurrencyYAxis,
		isDimensionAxisMode,
		isDimensionChartReady,
		isDimensionMultiPeriod,
		isLight,
		isMultiMetric,
		isYearMonthsMode,
		trendDimensionFieldLabel,
		trendDualFullPeriod,
		trendAxisMode,
		trendBarOrientation,
		resolvedMetricSeriesTypes,
		trendChartType,
		trendLineShape,
		multiMetricTimeline,
		periodYoyGranularity,
		primaryYAxisMetric,
		showPointLabels,
		timelines,
		yearMonthsSeriesByField,
		yearMonthsSeriesByMetric,
		trendYearSeriesColors,
	]);

	const hasChartData = isMapChart
		? !isDimensionAxisMode
		: isDimensionAxisMode
			? isDimensionChartReady
			: isYearMonthsMode
				? isMultiMetric
					? trendDualFullPeriod
						? Boolean(activePanels.some((panel) => activeYAxisMetrics.some((metric) => yearMonthsSeriesByMetric?.[panel.key]?.[metric]?.series?.length)))
						: Boolean(activeYAxisMetrics.some((metric) => yearMonthsSeriesByMetric?.single?.[metric]?.series?.length))
					: trendDualFullPeriod
						? Boolean(activePanels.some((panel) => yearMonthsSeriesByField?.[panel.key]?.series?.length))
						: Boolean(yearMonthsSeriesByField?.[resolveTrendPeriodValueField(primaryYAxisMetric)]?.series?.length)
				: isMultiMetric
					? Boolean(multiMetricTimeline?.length && (customCompareCount > 0 ? multiMetricTimeline.length >= 2 : multiMetricTimeline.length >= 1))
					: Boolean(timelines?.length && (customCompareCount > 0 ? timelines.length >= 2 : timelines.length >= 1));

	const summaryStats = useMemo(() => {
		if (highlightedSummary) return highlightedSummary;

		if (isYearMonthsMode && normalizedYoy?.years?.length) {
			const grid = resolvePeriodYoyGrid(normalizedYoy, selectedPhase, periodYoyGranularity);
			const maxPeriods = periodYoyGranularity === PERIOD_YOY_GRANULARITY.QUARTER ? 4 : 12;
			const indices =
				Array.isArray(periodYoyIndices) && periodYoyIndices.length > 0 && periodYoyIndices.length < maxPeriods
					? periodYoyIndices
					: null;
			const salesTotals = [];
			const orderCounts = [];
			const compareLabels = [];
			for (const year of normalizedYoy.years) {
				const cells = grid?.[year] ?? [];
				const periodIndices =
					indices ??
					Array.from({ length: periodYoyGranularity === PERIOD_YOY_GRANULARITY.QUARTER ? 4 : 12 }, (_, index) => index);
				const rows = periodIndices
					.filter((index) => !isFutureCalendarPeriodIndex(year, index, periodYoyGranularity))
					.map((index) => cells[index]);
				let salesTotal = 0;
				let orderCount = 0;
				for (const cell of rows) {
					orderCount += Number(cell?.count) || 0;
					salesTotal += Number(cell?.totalPrice) || 0;
				}
				salesTotals.push(salesTotal);
				orderCounts.push(orderCount);
				compareLabels.push(`${year}年`);
			}
			return { salesTotal: salesTotals, orderCount: orderCounts, compareLabels };
		}

		if (!isYearMonthsMode && compares?.length >= 1) {
			const salesTotals = [];
			const orderCounts = [];
			const compareLabels = [];
			for (const item of compares) {
				const overview = item?.lensData?.overview ?? item?.overview ?? null;
				const phaseRow = selectedPhase ? item?.lensData?.phases?.[selectedPhase] ?? null : null;
				const row = phaseRow ?? overview;
				orderCounts.push(getPrimaryPhaseRowMetricValue(row, ORDER_PRIMARY_PHASE_METRICS.COUNT));
				salesTotals.push(getPrimaryPhaseRowMetricValue(row, ORDER_PRIMARY_PHASE_METRICS.TOTAL_PRICE));
				compareLabels.push(
					resolvePeriodCompareCategoryLabel({
						kind: item.kind,
						title: item.title,
						periodLabel: item.periodLabel,
						startDate: item.periodStartDate,
						endDate: item.periodEndDate,
						axisLabel: item.periodLabel || item.title,
					}),
				);
			}
			return { salesTotal: salesTotals, orderCount: orderCounts, compareLabels };
		}

		let salesTotal = 0;
		let orderCount = 0;
		for (const order of orders ?? []) {
			if (selectedPhase && getOrderStatisticsPrimaryPhase(order) !== selectedPhase) continue;
			orderCount += 1;
			salesTotal += Number(order.totalPrice) || 0;
		}
		return { salesTotal, orderCount };
	}, [compares, highlightedSummary, isYearMonthsMode, normalizedYoy, orders, periodYoyGranularity, periodYoyIndices, selectedPhase]);

	const panelDisplayOptions = useMemo(
		() =>
			isMapChart
				? panelOptions
				: panelOptions.map((panel) => {
						if (!panel.option) return panel;
						const withSummary = applyChartSummaryLegend(panel.option, summaryStats, chartTheme);
						return {
							...panel,
							option: isPreview ? tightenPreviewChartGrid(withSummary) : withSummary,
						};
					}),
		[chartTheme, isMapChart, isPreview, panelOptions, summaryStats],
	);

	if (isLoading) {
		if (isPreview) {
			return (
				<div className={['statistics-trend-chart statistics-trend-chart--preview'].join(' ')} style={previewFrameStyle}>
					<StatisticsCardLoading className='statistics-lite-chart__loading statistics-lite-chart__loading--fill' fill />
				</div>
			);
		}

		return <StatisticsCardLoading className='statistics-lite-chart__loading' style={{ minHeight: height }} />;
	}

	/** 制作区预览：先出本期图，同比/环比在预览卡片内另选，不在此挡图 */
	if (compareAnalysisDisabled && !allowChartWithoutCompareAnalysis) {
		const blockedHint = compareAnalysisDisabledHint?.trim() || STATISTICS_EMPTY_CHART_COMPARE_IRREGULAR_PERIOD;
		return (
			<div className={['statistics-trend-chart', isPreview ? 'statistics-trend-chart--preview' : null].filter(Boolean).join(' ')}>
				<StatisticsChartFrame style={previewFrameStyle ?? { minHeight: height }}>
					<StatisticsChartEmpty fill>{blockedHint}</StatisticsChartEmpty>
				</StatisticsChartFrame>
			</div>
		);
	}

	if (isMapChart) {
		if (isDimensionAxisMode) {
			return (
				<div className={['statistics-trend-chart', isPreview ? 'statistics-trend-chart--preview' : null].filter(Boolean).join(' ')}>
					<StatisticsChartFrame style={previewFrameStyle ?? { minHeight: height }}>
						<div className='statistics-pipeline-page__chart-empty statistics-pipeline-page__chart-empty--fill'>地图对比仅支持横轴为「时间」；请切换横轴或改选折线/柱状图</div>
					</StatisticsChartFrame>
				</div>
			);
		}

		return (
			<div className={['statistics-trend-chart', isPreview ? 'statistics-trend-chart--preview' : null].filter(Boolean).join(' ')}>
				<TrendMapChart
					orders={orders}
					compares={compares}
					trendYearMonthsMode={trendYearMonthsMode}
					trendGranularity={trendGranularity}
					trendPeriodIndices={trendPeriodIndices}
					trendMonthIndices={trendMonthIndices}
					trendPeriodYoy={trendPeriodYoy}
					trendDateField={trendDateField}
					selectedPhase={selectedPhase}
					highlightProvinceNames={highlightProvinceNames}
					highlightAnchorCityRows={highlightAnchorCityRows}
					isLoading={isLoading}
					animationKey={animationKey}
					height={fillPreview && !previewHeight ? '100%' : previewHeight || height}
					fillSlot={fillPreview}
					enableOrderDrill={enableOrderDrill}
					onDrillSelect={onDrillSelect}
				/>
			</div>
		);
	}

	if (!hasChartData) {
		return (
			<div className={['statistics-trend-chart', isPreview ? 'statistics-trend-chart--preview' : null].filter(Boolean).join(' ')}>
				<StatisticsChartFrame style={previewFrameStyle ?? { minHeight: height }}>
					<StatisticsChartEmpty fill>
						{isDimensionAxisMode
								? allowChartWithoutCompareAnalysis
									? STATISTICS_EMPTY_CHART
									: STATISTICS_EMPTY_CHART_COMPARE
								: isYearMonthsMode
									? trendPeriodYoy
										? STATISTICS_EMPTY_CHART
										: '未能加载各月/各季走势，请稍后刷新页面重试'
									: !hasAnchor
										? allowChartWithoutCompareAnalysis
											? STATISTICS_EMPTY_CHART
											: '先选基准区间，再添加至少 1 个对比段'
										: customCompareCount === 0
											? allowChartWithoutCompareAnalysis
												? '本期已可预览；可在上方选择同比或环比叠加对比段'
												: '基准已选，请点按年或按季/月对比后自选对比段'
											: '至少需基准 + 1 个对比区间'}
					</StatisticsChartEmpty>
				</StatisticsChartFrame>
			</div>
		);
	}

	const effectiveChartHeight = isPreview || lockChartHeight ? previewHeight || height : isDimensionAxisMode ? dimensionChartHeight : height;
	const frameStyle = isPreview ? previewFrameStyle : { minHeight: effectiveChartHeight };
	const echartStyle = isPreview ? { height: '100%', width: '100%', minHeight: 0 } : { minHeight: effectiveChartHeight, width: '100%' };

	return (
		<div
			className={['statistics-trend-chart', isPreview ? 'statistics-trend-chart--preview' : null].filter(Boolean).join(' ')}
			style={isPreview ? { ...previewFrameStyle, position: 'relative' } : undefined}
		>
			{showPaintSkeleton ? (
				<div className='statistics-chart-preview-paint-gate' aria-hidden='true'>
					<StatisticsCardLoading className='statistics-lite-chart__loading statistics-lite-chart__loading--fill' fill />
				</div>
			) : null}
			<div
				className={[
					'statistics-trend-charts__grid',
					showDualFullPeriodGrid ? 'statistics-trend-charts__grid--dual-full' : null,
					activePanels.length === 1 ? 'statistics-trend-charts__grid--single' : null,
				]
					.filter(Boolean)
					.join(' ')}
				style={showPaintSkeleton ? { visibility: 'hidden' } : undefined}
				aria-hidden={showPaintSkeleton || undefined}
			>
				{panelDisplayOptions.map((panel) => (
					<section key={panel.key} className='statistics-trend-charts__item'>
						{showPanelTitles ? <h4 className='statistics-trend-charts__item-title mb-0'>{panel.title}</h4> : null}
						{panel.option ? (
							<StatisticsChartFrame style={frameStyle}>
								<ReactEchart
									key={`compare-${panel.key}-${activeYAxisMetrics.join(',')}-${trendChartType}-${JSON.stringify(resolvedMetricSeriesTypes)}-${trendLineShape}-${trendBarOrientation}-${animationKey}-${showPointLabels ? 'labels' : 'plain'}`}
									echarts={echarts}
									option={panel.option}
									style={echartStyle}
									opts={TREND_ECHART_OPTS}
									onEvents={!isMapChart ? chartDrillEvents : undefined}
									onChartReady={onPreviewChartReady}
								/>
							</StatisticsChartFrame>
						) : (
							<StatisticsChartEmpty fill>{STATISTICS_EMPTY_CHART}</StatisticsChartEmpty>
						)}
					</section>
				))}
			</div>
		</div>
	);
};

TrendChart.propTypes = {
	orders: PropTypes.array,
	highlightProvinceNames: PropTypes.arrayOf(PropTypes.string),
	highlightAnchorCityRows: PropTypes.array,
	compares: PropTypes.arrayOf(PropTypes.object),
	trendYearMonthsMode: PropTypes.bool,
	trendDualFullPeriod: PropTypes.bool,
	trendGranularity: PropTypes.string,
	trendMonthIndices: PropTypes.arrayOf(PropTypes.number),
	trendPeriodIndices: PropTypes.arrayOf(PropTypes.number),
	trendPeriodYoy: PropTypes.object,
	trendDimensionCountSeries: PropTypes.object,
	trendDimensionTotalPriceSeries: PropTypes.object,
	trendDimensionSeriesByMetric: PropTypes.object,
	trendDimensionFieldLabel: PropTypes.string,
	trendAxisMode: PropTypes.string,
	trendDateField: PropTypes.string,
	trendYAxisMetric: PropTypes.string,
	trendYAxisMetrics: PropTypes.arrayOf(PropTypes.string),
	trendChartType: PropTypes.string,
	trendLineShape: PropTypes.string,
	trendBarOrientation: PropTypes.string,
	trendMetricSeriesTypes: PropTypes.object,
	trendYearSeriesColors: PropTypes.object,
	selectedPhase: PropTypes.string,
	isLoading: PropTypes.bool,
	animationKey: PropTypes.string,
	height: PropTypes.string,
	lockChartHeight: PropTypes.bool,
	previewHeight: PropTypes.string,
	fillPreview: PropTypes.bool,
	showPointLabels: PropTypes.bool,
	enableOrderDrill: PropTypes.bool,
	onDrillSelect: PropTypes.func,
	compareAnalysisDisabled: PropTypes.bool,
	compareAnalysisDisabledHint: PropTypes.string,
	allowChartWithoutCompareAnalysis: PropTypes.bool,
};

export default memo(TrendChart);
